import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar } from "lucide-react";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { InterestCta, TextLink } from "../components/Bits";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { SITE_URL } from "../lib/constants";
import { getEdition, editionJsonLd } from "../lib/editions";

export default function EditionDetail() {
  const { slug } = useParams();
  const edition = getEdition(slug);
  if (!edition) return <Navigate to={ROUTES.editions.path} replace />;

  const path = `${ROUTES.editions.path}${edition.slug}/`;
  const route = {
    path,
    title: edition.title
      ? `${edition.title}, ${edition.city} — ${edition.dateLabel} | Antara`
      : `Antara — ${edition.dateLabel}, ${edition.city} | Bhajan Jamming Recap`,
    desc: edition.seoDesc,
  };
  const cover = edition.photos[0];

  return (
    <>
      <Seo route={route} type="article" image={`${SITE_URL}${cover.src.replace(/^https?:\/\/[^/]+/, "")}`} />
      <JsonLd
        data={[
          ...editionJsonLd(edition, `${SITE_URL}${path}`),
          breadcrumbs([
            { name: ROUTES.editions.crumb, path: ROUTES.editions.path },
            { name: `${edition.dateLabel}, ${edition.city}`, path },
          ]),
        ]}
      />

      <PageHero
        eyebrow={`${edition.title ? `${edition.title} · ` : ""}${edition.dateLabel} · ${edition.city}`}
        title={<span className="gold-grad italic">{edition.venue}</span>}
        intro={edition.tagline}
      >
        {edition.soldOut && (
          <p className="mt-5 inline-flex rounded-full px-4 py-1.5 font-body text-xs font-semibold tracking-[0.18em] uppercase sold-out-badge">Sold out</p>
        )}
      </PageHero>

      <section className="bg-cream py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <Link to={ROUTES.editions.path} className="inline-flex items-center gap-2 font-body text-sm text-amber hover:text-gold transition">
              <ArrowLeft size={15} /> All editions
            </Link>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-body text-sm text-ink-2/85">
              <span className="flex items-center gap-2"><Calendar size={15} className="text-gold" /> {edition.dateLabel}</span>
              <span className="flex items-center gap-2"><MapPin size={15} className="text-gold" /> {edition.venue}, {edition.city}</span>
            </div>
            <p className="mt-8 font-body text-lg text-ink-2/90 leading-relaxed">{edition.recap}</p>
            <p className="mt-4 font-body text-base text-ink-2/80 leading-relaxed">
              New to the format? Here's <TextLink to={ROUTES.pillar.path}>what bhajan jamming is</TextLink>.
            </p>
            <LotusDivider />
          </Reveal>

          {/* Setlist — placeholder until real data supplied */}
          <Reveal>
            <Eyebrow>Setlist</Eyebrow>
            {edition.setlist.length > 0 ? (
              <ol className="mt-4 space-y-2 font-body text-ink-2/85">
                {edition.setlist.map((s, i) => (
                  <li key={i} className="flex gap-3"><span className="text-gold">{i + 1}.</span>{s}</li>
                ))}
              </ol>
            ) : (
              /* TODO_CONTENT: add the real setlist for this edition */
              <p className="mt-3 font-serif-italic text-amber">Setlist coming soon.</p>
            )}
          </Reveal>
        </div>
      </section>

      {/* Photos */}
      <section className="bg-[#0F0A07] py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <Eyebrow>From the night</Eyebrow>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {edition.photos.map((ph, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <div className="aspect-square overflow-hidden rounded-xl border" style={{ borderColor: "rgba(201,169,97,0.2)" }}>
                  <img src={ph.src} alt={ph.alt} width={ph.width} height={ph.height} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
          {edition.galleryId && (
            <p className="mt-8 text-center">
              <Link to={ROUTES.gallery.path} className="font-body text-sm underline underline-offset-4" style={{ color: "#E5C880" }}>
                See every photograph from the night, free to download
              </Link>
            </p>
          )}
          <Reveal delay={0.15}>
            <div className="mt-14 text-center">
              <InterestCta tone="dark" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
